import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, BehaviorSubject, throwError, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { Topic, TopicsFilter, Topics, MyCullenBlocks, TopicContent, MyCullenList } from '../../model/topics.model';
import { InterceptorSkipHeader, ConfigAssetLoaderService, OperationResult } from '@icullen/api/shared';
import { Notifications } from '../../model/notifications.model';

@Injectable({
  providedIn: 'root',
})
export class TopicsService {
  private readonly DELIVERY_TOPICS_API_BASE_URL;
  private readonly DELIVERY_NOTIFICATIONS_API_BASE_URL;

  private followedTopicsSubject = new BehaviorSubject<Topic[]>([]);
  public followedTopics$ = this.followedTopicsSubject.asObservable();

  constructor(private httpClient: HttpClient, private configService: ConfigAssetLoaderService) {
    const deliveryServiceBaseUrl = this.configService.getConfig().deliveryServiceBaseUrl;
    this.DELIVERY_TOPICS_API_BASE_URL = `${deliveryServiceBaseUrl}topics`;
    this.DELIVERY_NOTIFICATIONS_API_BASE_URL = `${deliveryServiceBaseUrl}users`;
  }

  getTopic(filter: TopicsFilter, id: string): Observable<Topic> {
    const params = new HttpParams()
      .set('keyword', filter.keyword ?? '')
      .set('page', (filter.page ?? 0).toString())
      .set('limit', (filter.limit ?? 25).toString())
      .set('sortBy', filter.sortBy ?? 'publicationDate')
      .set('sortDirection', filter.sortDirection ?? 'desc');
    return this.httpClient.get<Topic>(`${this.DELIVERY_TOPICS_API_BASE_URL}/${id}`, { params }).pipe(
      catchError(error => {
        console.error(`Error fetching topic ${id}:`, error);
        return throwError(() => error);
      })
    );
  }

  getTopicRelatedTopics(id: string): Observable<Topic> {
    return this.httpClient.get<Topic>(
      `${this.DELIVERY_TOPICS_API_BASE_URL}/${id}/related-topics`,
      InterceptorSkipHeader
    );
  }

  getMostReadReportsForTopic(id: string, contentType: string): Observable<TopicContent[]> {
    let params = new HttpParams().set('limit', '3');
    if (contentType) {
      params = params.set('contentType', contentType);
    }
    return this.httpClient
      .get<TopicContent[]>(`${this.DELIVERY_TOPICS_API_BASE_URL}/${id}/most-read-reports`, {
        params,
        ...InterceptorSkipHeader,
      })
      .pipe(
        map(content => {
          const seenTitles = new Set<string>();
          return content.filter(item => {
            if (seenTitles.has(item.title)) {
              return false;
            }
            seenTitles.add(item.title);
            return true;
          });
        })
      );
  }

  getRecommendedTopics(): Observable<Topic[]> {
    return this.httpClient.get<Topic[]>(`${this.DELIVERY_TOPICS_API_BASE_URL}/recommended`, InterceptorSkipHeader);
  }

  getTopics(filter: TopicsFilter): Observable<Topics> {
    const params = new HttpParams()
      .set('keyword', filter.keyword ?? '')
      .set('page', (filter.page ?? 0).toString())
      .set('limit', (filter.limit ?? 25).toString())
      .set('sortBy', filter.sortBy ?? '')
      .set('sortDirection', filter.sortDirection ?? 'desc');
    return this.httpClient
      .get<Topics>(`${this.DELIVERY_TOPICS_API_BASE_URL}`, { params, ...InterceptorSkipHeader })
      .pipe(
        catchError(error => {
          console.error('Error fetching topics:', error);
          return throwError(() => error);
        })
      );
  }

  getFollowedTopics(filter: TopicsFilter = { keyword: '' }, forceRefresh = false): Observable<Topic[]> {
    if (forceRefresh || this.followedTopicsSubject.value.length === 0) {
      this.loadFollowedTopics(filter);
    }
    return this.followedTopics$;
  }

  private loadFollowedTopics(filter: TopicsFilter): void {
    const params = new HttpParams().set('keyword', filter.keyword ?? '').set('followed', 'true');
    this.httpClient
      .get<Topics>(`${this.DELIVERY_TOPICS_API_BASE_URL}`, { params, ...InterceptorSkipHeader })
      .pipe(
        map(response => (Array.isArray(response) ? response : response.topics || [])),
        catchError(error => {
          console.error('Error loading followed topics:', error);
          return throwError(() => error);
        })
      )
      .subscribe(topics => {
        this.followedTopicsSubject.next(topics);
      });
  }

  getUnfollowedTopics(filter: TopicsFilter): Observable<Topic[]> {
    const params = new HttpParams().set('keyword', filter.keyword ?? '').set('followed', 'false');
    return this.httpClient
      .get<Topics>(`${this.DELIVERY_TOPICS_API_BASE_URL}`, { params, ...InterceptorSkipHeader })
      .pipe(
        map(response => (Array.isArray(response) ? response : response.topics || [])),
        catchError(error => {
          console.error('Error loading unfollowed topics:', error);
          return throwError(() => error);
        })
      );
  }

  followTopic(id: string): Observable<OperationResult> {
    return this.httpClient
      .post<OperationResult>(`${this.DELIVERY_TOPICS_API_BASE_URL}/${id}/follow`, InterceptorSkipHeader)
      .pipe(
        tap(result => {
          if (result.succeeded) {
            this.loadFollowedTopics({ keyword: '' });
          }
        }),
        catchError(error => {
          console.error(`Error following topic ${id}:`, error);
          return throwError(() => error);
        })
      );
  }

  unfollowTopic(id: string): Observable<OperationResult> {
    return this.httpClient
      .delete<OperationResult>(`${this.DELIVERY_TOPICS_API_BASE_URL}/${id}/follow`, InterceptorSkipHeader)
      .pipe(
        tap(result => {
          if (result.succeeded) {
            const currentFollowed = this.followedTopicsSubject.value;
            this.followedTopicsSubject.next(currentFollowed.filter(t => t.id !== id));
          }
        }),
        catchError(error => {
          console.error(`Error unfollowing topic ${id}:`, error);
          return throwError(() => error);
        })
      );
  }

  refreshTopics(filter: TopicsFilter = { keyword: '' }): void {
    this.loadFollowedTopics(filter);
  }

  getMyCullenBlockView(filter: TopicsFilter): Observable<MyCullenBlocks[]> {
    const params = new HttpParams()
      .set('keyword', filter.keyword ?? '')
      .set('page', (filter.page ?? 0).toString())
      .set('limit', (filter.limit ?? 25).toString())
      .set('sortBy', filter.sortBy ?? 'publicationDate')
      .set('sortDirection', filter.sortDirection ?? 'desc')
      .set('view', 'group');
    return this.httpClient.get<MyCullenBlocks[]>(`${this.DELIVERY_TOPICS_API_BASE_URL}/contents`, {
      params,
    });
  }

  getMyCullenListView(filter: TopicsFilter): Observable<MyCullenList[]> {
    const params = new HttpParams()
      .set('keyword', filter.keyword ?? '')
      .set('page', (filter.page ?? 0).toString())
      .set('limit', (filter.limit ?? 25).toString())
      .set('sortBy', filter.sortBy ?? 'publicationDate')
      .set('sortDirection', filter.sortDirection ?? 'desc')
      .set('view', 'list');
    return this.httpClient.get<MyCullenList[]>(`${this.DELIVERY_TOPICS_API_BASE_URL}/contents`, {
      params,
      ...InterceptorSkipHeader,
    });
  }

  getNotifications(): Observable<Notifications> {
    const params = new HttpParams().set('limit', '6');
    return this.httpClient
      .get<Notifications>(`${this.DELIVERY_NOTIFICATIONS_API_BASE_URL}/notifications`, {
        params,
        ...InterceptorSkipHeader,
      })
      .pipe(
        catchError(error => {
          console.error('Error fetching notifications:', error);
          const emptyNotifications: Notifications = {
            username: '',
            notificationsViewedAt: new Date(),
            notifications: [],
            unseenCount: 0,
          };
          return of(emptyNotifications);
        })
      );
  }

  markNotificationsAsViewed(): Observable<any> {
    return this.httpClient
      .post(`${this.DELIVERY_NOTIFICATIONS_API_BASE_URL}/notifications-view`, InterceptorSkipHeader)
      .pipe(
        catchError(error => {
          console.error('Error marking notifications as viewed:', error);
          return of({ success: false, error: 'Failed to mark notifications as viewed' });
        })
      );
  }
}
